import { createSlice } from '@reduxjs/toolkit';

import type {
  RarDataAPIPagination,
  RarDataAPIReviewItem,
} from 'src/services/rar-data-api/types/RarDataAPIReviews';
import { logger } from 'src/utils/serverLogger';
import { adaptFeefoRatingBreakdownToStore } from './adapters/adaptFeefoRatingBreakdownToStore';
import { adaptFeefoReviewsToStore } from './adapters/adaptFeefoReviewsToStore';
import { adaptProductInsightsToStore } from './adapters/adaptProductInsightsToStore';
import { adaptReviewSummaryToStore } from './adapters/adaptReviewSummaryToStore';
import {
  requestAiSummary,
  requestPhotoReviews,
  requestProductReviews,
  requestReviewsPage,
} from './thunks';
import type {
  MaybeSettled,
  ProductReview,
  ReviewRatingFilter,
  ReviewsListMeta,
  ReviewsSummaryAiSummary,
  ReviewsSummaryProductInsights,
  ReviewsSummaryRatingBreakdown,
} from './types';

export type ReviewsPages = Record<number, RarDataAPIReviewItem[]> | null;

/**
 * The Feefo-direct reviews list. Holds pages for one rating filter at a time; a filter
 * change clears them and returns to page 1. Separate from the rar-data-api
 * `pages`/`pagination` fields, which stay until Increment C removes that service.
 */
export interface ReviewsListState {
  /** A rejection or a settled-but-empty response. */
  error: boolean;
  /**
   * The unfiltered first page, which the preview row on the page shows. Kept apart from
   * `pages`, which follow the drawer's star filter: filtering the drawer must not change the
   * row behind it. Null until it lands.
   */
  firstPage: ProductReview[] | null;
  loading: boolean;
  meta: ReviewsListMeta | null;
  /** Pages of the active filter, keyed by page number. */
  pages: Record<number, ProductReview[]>;
  /** The filter the cached pages belong to. */
  rating: ReviewRatingFilter;
  /**
   * The latest list request. An older one that settles after it is not filed, whichever page
   * or filter it was for: a retry through a cold Feefo can land many seconds late, and two
   * requests for the same page can overlap, so the page number alone cannot order them.
   */
  requestId: string | null;
  /** The product's total from the last unfiltered page; `meta` follows the filter, this does not. */
  totalCount: number | null;
  /**
   * The page being shown or fetched, so a click changes the screen at once. Deliberately not
   * `meta.currentPage`, which is only the page that last arrived.
   */
  visiblePage: number;
}

export interface ProductReviewsState {
  /**
   * Thunk request ids started for the product the page is showing; `resetForProduct` empties
   * it. A request can outlive a client-side navigation (a retry through a cold Feefo lands
   * many seconds late), so the reducers file a result only if its request is still here.
   * The product code alone would not do: going A to B and back to A starts a second request
   * for A, and the first must not settle over it.
   */
  activeRequestIds: string[];
  aiSummary: MaybeSettled<ReviewsSummaryAiSummary>;
  averageProductRating: number | null;
  list: ReviewsListState;
  /**
   * A failure of the rar-data-api review list fetch only. The summary fields
   * (aiSummary, productInsights, ratingBreakdown) signal their own failures by
   * settling to null instead, and the Feefo list has its own `list.error`.
   */
  listError: boolean;
  numberOfReviews: number | null;
  pages: ReviewsPages;
  pagination: RarDataAPIPagination | null;
  /**
   * The most recent reviews carrying a photo, newest first as Feefo returns them. A page
   * of its own, not a view of `list`: the list is every review and photos are rare. Null
   * once the request has failed, which hides the strip like a product with no photos.
   */
  photoReviews: MaybeSettled<ProductReview[]>;
  /**
   * The product the client-fetched fields were requested for, taken from the requests. The
   * selectors compare it with the product the page is showing: a client-side navigation
   * renders the new product once before `useProductReviewsFetch`'s effect resets this slice,
   * and the props deep-merge leaves the previous product's fields in place for that render.
   */
  productCode: number | null;
  productInsights: MaybeSettled<ReviewsSummaryProductInsights>;
  ratingBreakdown: MaybeSettled<ReviewsSummaryRatingBreakdown>;
}

export const initialListState: Readonly<ReviewsListState> = {
  error: false,
  firstPage: null,
  loading: false,
  meta: null,
  totalCount: null,
  pages: {},
  requestId: null,
  rating: null,
  visiblePage: 1,
};

export const initialState: Readonly<ProductReviewsState> = {
  activeRequestIds: [],
  aiSummary: undefined,
  list: initialListState,
  listError: false,
  numberOfReviews: null,
  pages: null,
  pagination: null,
  averageProductRating: null,
  photoReviews: undefined,
  productCode: null,
  productInsights: undefined,
  ratingBreakdown: undefined,
};

/** Whether a settled request is still current, removing it from the active set if so. */
const claimRequest = (state: Pick<ProductReviewsState, 'activeRequestIds'>, requestId: string) => {
  if (!state.activeRequestIds.includes(requestId)) {
    return false;
  }

  state.activeRequestIds = state.activeRequestIds.filter((id) => id !== requestId);

  return true;
};

// Reviews data is non-critical: every failure path settles the field to null
// and logs at warn, never error, so the page renders without it and a Feefo
// blip does not page anyone. See the DISC-4325 spec's failure policy.
export const { actions, reducer } = createSlice({
  name: 'reviews',
  initialState,
  reducers: {
    /**
     * Client-side navigation keeps the store and deep-merges the new page's props in, and
     * this slice's client-fetched fields are not in those props, so without this the next
     * product would start with the previous one's rating and insights. The AI summary is
     * server-rendered per page, so it arrives in the props and is left alone. Emptying
     * `activeRequestIds` is what makes the previous product's in-flight requests stale.
     */
    resetForProduct: (state) => {
      Object.assign(state, initialState, { aiSummary: state.aiSummary });
    },
    /**
     * Moves to a page already in `pages` without fetching it. A failed request for another
     * page is no longer what is on screen, so its error goes with it.
     */
    showPage: (state, { payload }: { payload: number }) => {
      state.list.visiblePage = payload;
      state.list.error = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(requestProductReviews.pending, (state, { meta }) => {
        state.activeRequestIds.push(meta.requestId);
        state.productCode = meta.arg.productCode;
      })
      .addCase(requestProductReviews.fulfilled, (state, { meta, payload }) => {
        if (!claimRequest(state, meta.requestId)) {
          return;
        }

        const { averageProductRating, numberOfReviews, pagination, reviews } = payload;

        state.pages = { ...(state.pages || {}), [pagination.currentPage]: reviews };
        state.pagination = { ...state.pagination, ...pagination };
        state.averageProductRating = averageProductRating || 0;
        state.numberOfReviews = numberOfReviews;
        state.listError = false;
      })
      .addCase(requestProductReviews.rejected, (state, action) => {
        logger.error('Error requesting product reviews', action.error);

        if (!claimRequest(state, action.meta.requestId)) {
          return;
        }

        state.pages = { ...(state.pages || {}) };
        state.listError = true;
        state.averageProductRating = 0;
        state.numberOfReviews = 0;
      })
      .addCase(requestAiSummary.fulfilled, (state, { payload }) => {
        state.aiSummary = payload ? adaptReviewSummaryToStore(payload) : null;
      })
      .addCase(requestAiSummary.rejected, (state, action) => {
        logger.warn('Error requesting AI summary', action.error);
        state.aiSummary = null;
      })
      .addCase(requestPhotoReviews.pending, (state, { meta }) => {
        state.activeRequestIds.push(meta.requestId);
        state.productCode = meta.arg.productCode;
      })
      .addCase(requestPhotoReviews.fulfilled, (state, { meta, payload }) => {
        if (!claimRequest(state, meta.requestId)) {
          return;
        }

        state.photoReviews = payload ? adaptFeefoReviewsToStore(payload.page).reviews : null;
      })
      .addCase(requestPhotoReviews.rejected, (state, action) => {
        logger.warn('Error requesting the reviews with photos', action.error);

        if (!claimRequest(state, action.meta.requestId)) {
          return;
        }

        state.photoReviews = null;
      })
      .addCase(requestReviewsPage.pending, (state, { meta }) => {
        state.activeRequestIds.push(meta.requestId);
        state.productCode = meta.arg.productCode;
        state.list.requestId = meta.requestId;
        const rating = meta.arg.rating ?? null;

        state.list.visiblePage = meta.arg.page;

        // A different filter is a different list: clear the cache on the request, not the response.
        if (rating !== state.list.rating) {
          state.list.meta = null;
          state.list.pages = {};
          state.list.rating = rating;
        }

        state.list.error = false;
        state.list.loading = true;
      })
      .addCase(requestReviewsPage.fulfilled, (state, { meta, payload }) => {
        // Started before a navigation, or before a newer request for the same product: not
        // this product's data, summary included.
        if (!claimRequest(state, meta.requestId)) {
          return;
        }
        const rating = meta.arg.rating ?? null;

        const isLatest = meta.requestId === state.list.requestId;

        // The summary is per product, not per page, so it is filed from any of this product's
        // requests. A superseded one only fills what is still missing, since a retry that
        // succeeded may already have delivered the breakdown the older attempt timed out on.
        // An unfiltered first page that settled with nothing marks it failed, unless a newer
        // request has already delivered it.
        if (payload?.summary) {
          if (isLatest || state.productInsights == null) {
            state.productInsights = payload.summary.productInsights
              ? adaptProductInsightsToStore(payload.summary.productInsights)
              : null;
          }

          if (isLatest || state.ratingBreakdown == null) {
            state.ratingBreakdown = payload.summary.ratingBreakdown
              ? adaptFeefoRatingBreakdownToStore(payload.summary.ratingBreakdown)
              : null;
          }
        } else if (
          !payload &&
          meta.arg.page === 1 &&
          rating === null &&
          state.ratingBreakdown === undefined
        ) {
          state.productInsights = null;
          state.ratingBreakdown = null;
        }

        const response = payload?.page;
        const adapted = response ? adaptFeefoReviewsToStore(response) : null;

        // The product-wide facts an unfiltered page carries, like the summary: filed from a
        // superseded request too when still missing, since a filter picked before the first
        // page landed would otherwise leave the preview row on its skeleton and the heading
        // without a count.
        if (adapted && rating === null) {
          if (meta.arg.page === 1 && (isLatest || state.list.firstPage === null)) {
            state.list.firstPage = adapted.reviews;
          }

          if (isLatest || state.list.totalCount === null) {
            state.list.totalCount = adapted.meta.totalCount;
          }
        }

        // Superseded by a newer list request, so no longer the list's data. Checked before
        // `loading` is touched, since that belongs to the newer request.
        if (!isLatest) {
          return;
        }

        state.list.loading = false;

        if (!adapted) {
          // Only for the page on screen: the customer may have moved back to a cached page
          // while this one was in flight, and that page has nothing wrong with it.
          state.list.error = meta.arg.page === state.list.visiblePage;

          return;
        }

        const { meta: listMeta, reviews } = adapted;
        const requestedPage = meta.arg.page;

        // Filed under the page asked for, not the page Feefo reports: the display reads
        // `pages[visiblePage]`, so a mismatch (Feefo omitting `current_page`, say) leaves it
        // empty and refetching in a loop. `currentPage` is overridden for the same reason.
        state.list.meta = { ...listMeta, currentPage: requestedPage };
        state.list.pages[requestedPage] = reviews;
      })
      .addCase(requestReviewsPage.rejected, (state, action) => {
        logger.warn('Error requesting a page of Feefo reviews', action.error);

        if (!claimRequest(state, action.meta.requestId)) {
          return;
        }

        if (
          action.meta.arg.page === 1 &&
          !action.meta.arg.rating &&
          state.ratingBreakdown === undefined
        ) {
          state.productInsights = null;
          state.ratingBreakdown = null;
        }

        if (action.meta.requestId !== state.list.requestId) {
          return;
        }

        state.list.loading = false;
        state.list.error = action.meta.arg.page === state.list.visiblePage;
      });
  },
});
